import React, { useEffect } from 'react';
import moment from 'moment';
import {
    CheckCircle,
    LocalShipping,
    LocationOn,
    Person,
    Timelapse,
    TimeToLeave,
} from '@mui/icons-material';
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Grid,
    Typography,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import toast from 'react-hot-toast';
import { colors } from '../../assets/utils/colors';
import CustomCard2 from '../../components/cards/CustomCard2';
import OrdersSideBar from '../../components/sidebars/OrdersSideBar';
import OrderProductTable from '../../components/tables/OrderProductTable';
import PageLayout from '../layouts/PageLayout';

// -> rtk query and redux imports
import {
    useGetOneOrderQuery,
    useDeliverOrderMutation,
    useShipOrderMutation,
} from '../../api/orders/orderAPI';
import {
    setOrder,
    setOrderDelivered,
    setOrderShipped,
} from '../../features/orderSlice';

// -> helpers imports
import { getChipColor, numberWithCommas } from '../../utils/helpers';
import DataFetchingFeedback from '../../components/feedbacks/DataFetchingFeedback';
import { ordersSelector } from '../../features/selectors';
import NoDataFeedback from '../../components/feedbacks/NoDataFeedback';
import { removeSpecialChar } from '../../helpers/stringHelpers';

// !############### MAIN FUNC ####################
const OrderDetails = () => {
    // ################ CONST #################
    const dispatch = useDispatch();
    const { order_id } = useParams();

    // ############### Comp States ############

    // ############### Redux state ################
    const { orders } = useSelector(ordersSelector);

    // ################# RTK ##################
    const {
        data: order,
        isLoading,
        isSuccess,
        isError,
        refetch,
    } = useGetOneOrderQuery(order_id, {
        refetchOnMountOrArgChange: true,
    });
    const [deliverOrder, deliveryResult] = useDeliverOrderMutation();
    const [shipOrder, shipResult] = useShipOrderMutation();

    // -> check if order is fetched
    if (isSuccess) {
        dispatch(setOrder({ order: order.data }));
    }

    const data = useSelector((state) => state.orders.order);

    // -> trigger this function when user click on deliver order
    // -> it perform optimistic update first
    const deliverAnOrder = (order_id) => {
        dispatch(setOrderDelivered({ orderDelivered: true }));
        return deliverOrder(order_id);
    };

    // -> trigger this function when user click on ship order
    // -> it perform optimistic update first
    const shipAnOrder = (order_id) => {
        dispatch(setOrderShipped({ orderShipped: true }));
        return shipOrder(order_id);
    };

    useEffect(() => {
        if (deliveryResult.isError) {
            toast.error('Ooops! an orded not delivered');
            dispatch(setOrderDelivered({ orderDelivered: false }));
        }

        if (deliveryResult.isSuccess) {
            toast.success(deliveryResult?.data?.message);
            dispatch(setOrderDelivered({ orderDelivered: true }));
            refetch();
        }

        if (deliveryResult.isLoading) {
            toast.success('Please wait while delivering order', {
                icon: <CircularProgress size={15} color="secondary" />,
            });
        }
        if (shipResult.isError) {
            toast.error('Ooops! an orded not shipped');
            dispatch(setOrderDelivered({ orderDelivered: false }));
        }

        if (shipResult.isSuccess) {
            toast.success(shipResult?.data?.message);
            dispatch(setOrderDelivered({ orderDelivered: true }));
            refetch();
        }

        if (shipResult.isLoading) {
            toast.success('Please wait while shipping order', {
                icon: <CircularProgress size={15} color="secondary" />,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        deliveryResult.isError,
        deliveryResult.isLoading,
        deliveryResult.isSuccess,
        shipResult.isError,
        shipResult.isLoading,
        shipResult.isSuccess,
    ]);

    return (
        <>
            <PageLayout
                sidebar={<OrdersSideBar orders={orders} />}
                head="Order details"
            >
                {isLoading ? (
                    <CustomCard2 height={635}>
                        <DataFetchingFeedback />
                    </CustomCard2>
                ) : isError ? (
                    <CustomCard2 height={635}>
                        <NoDataFeedback message={'Order detail not found'} />
                    </CustomCard2>
                ) : (
                    <CustomCard2
                        title={`#${data.order_number}`}
                        height={640}
                        action={
                            <Chip
                                sx={{
                                    textTransform: 'capitalize',
                                }}
                                size='small'
                                label={removeSpecialChar(data.order_status, " ")}
                                color={getChipColor(data.order_status)}
                            />
                        }
                    >
                        <Grid container>
                            <Grid item sm={12}>
                                <OrderProductTable
                                    ordered_products={data.ordered_products}
                                />
                            </Grid>
                        </Grid>
                        <Grid container>
                            <Grid item sm={8}>
                                <Grid container>
                                    <Grid item sm={8}>
                                        <Box
                                            sx={{
                                                height: 200,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor1})`,
                                            }}
                                        >
                                            <Grid
                                                container
                                                spacing={4}
                                                padding={2}
                                            >
                                                <Grid item sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Box sx={{ p: 1 }}>
                                                            <Person
                                                                sx={{
                                                                    color:
                                                                        colors.primary,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                Customer
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {`${data.first_name} ${data.last_name}`}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Box sx={{ p: 1 }}>
                                                            <Timelapse
                                                                sx={{
                                                                    color:
                                                                        colors.primary,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                Ordered On
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {moment(
                                                                    data.order_date,
                                                                ).format(
                                                                    'MMM Do YY',
                                                                )}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Box sx={{ p: 1 }}>
                                                            <LocalShipping
                                                                sx={{
                                                                    color:
                                                                        colors.primary,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                Delivery option
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {data.delivery_type ===
                                                                'delivery'
                                                                    ? 'Door delivery'
                                                                    : 'Self pickup'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                                <Grid item sm={6}>
                                                    <Box
                                                        sx={{
                                                            display: 'flex',
                                                            alignItems:
                                                                'center',
                                                        }}
                                                    >
                                                        <Box sx={{ p: 1 }}>
                                                            <LocationOn
                                                                sx={{
                                                                    color:
                                                                        colors.primary,
                                                                }}
                                                            />
                                                        </Box>
                                                        <Box>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                    fontWeight:
                                                                        'bold',
                                                                }}
                                                            >
                                                                Delivered
                                                                address
                                                            </Typography>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 12,
                                                                }}
                                                            >
                                                                {data.pickup_branch
                                                                    ? data.pickup_branch
                                                                    : 'unavailable'}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Grid>
                                            </Grid>
                                        </Box>
                                    </Grid>
                                    <Grid item sm={4}>
                                        <Box
                                            sx={{
                                                height: 200,
                                                background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor1})`,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-around',
                                                flexDirection: 'column',
                                                p: 2,
                                            }}
                                        >
                                            {data.order_status === 'placed' && (
                                                <Button
                                                    variant="contained"
                                                    color="info"
                                                    fullWidth
                                                    className="app-btn"
                                                    onClick={() =>
                                                        shipAnOrder(
                                                            data.order_id,
                                                        )
                                                    }
                                                    disabled={
                                                        shipResult.isLoading
                                                    }
                                                >
                                                    <TimeToLeave
                                                        sx={{
                                                            fontSize: 18,
                                                            mr: 1,
                                                        }}
                                                    />
                                                    Ship order
                                                </Button>
                                            )}
                                            {data.order_status ===
                                                'shipped' && (
                                                <Button
                                                    variant="contained"
                                                    color="success"
                                                    fullWidth
                                                    className="app-btn"
                                                    onClick={() =>
                                                        deliverAnOrder(
                                                            data.order_id,
                                                        )
                                                    }
                                                    disabled={
                                                        deliveryResult.isLoading
                                                    }
                                                >
                                                    <CheckCircle
                                                        sx={{
                                                            fontSize: 18,
                                                            mr: 1,
                                                        }}
                                                    />
                                                    Deliver order
                                                </Button>
                                            )}
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Grid>
                            <Grid item sm={4}>
                                <Box
                                    sx={{
                                        height: 200,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        flexDirection: 'column',
                                        background: `linear-gradient(180deg, ${colors.bgColor1}, ${colors.bgColor4})`,
                                    }}
                                >
                                    <Typography
                                        sx={{
                                            fontSize: 14,
                                            fontStyle: 'italic',
                                            color: colors.primary,
                                        }}
                                    >
                                        Subtotal
                                        <sup
                                            style={{
                                                fontSize: 12,
                                                fontWeight: 'bold',
                                            }}
                                        >
                                            (TZS)
                                        </sup>
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontSize: 25,
                                            fontWeight: 'bold',
                                            color: colors.primary,
                                        }}
                                    >
                                        {numberWithCommas(data.total_amount)}
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </CustomCard2>
                )}
            </PageLayout>
        </>
    );
};

export default OrderDetails;
